import React, { useEffect, useRef } from "react";
import { useTheme } from "../context/ThemeContext";

/* Site-wide fixed background layer: draws faint twinkling "star" glyphs at
   random positions so the empty screen never looks fully static. It sits
   behind all page content (sections are translucent), so the effect shows
   through on every page, not just the hero. */

const ACCENT = {
  // --brand-cyan (dark theme)  #6B8EE8
  dark: { r: 107, g: 142, b: 232 },
  // --brand-navy (light theme) #2B50B8
  light: { r: 43, g: 80, b: 184 },
};

// =============================================================================
//  PARILTI (TWINKLE) — ayarlar. Aynı değerler AsciiFluidSimulation ile
//  tutarlıdır; buradan ayrıca ayarlanabilir.
// =============================================================================
const SPARKLE_RATE = 3.0;      // saniyede doğan parıltı sayısı
const SPARKLE_MAX = 50;        // aynı anda görülebilecek en fazla parıltı
const SPARKLE_ALPHA_MIN = 0.5; // minimum parlaklık (0..1)
const SPARKLE_ALPHA_MAX = 0.99; // maksimum parlaklık (0..1)
const SPARKLE_LIFE_MIN = 1.2;  // minimum ömür (saniye)
const SPARKLE_LIFE_MAX = 3.0;  // maksimum ömür (saniye)
const SPARKLE_FADE_IN = 0.25;  // yaşamın ne kadarı yumuşak belirmede
const SPARKLE_FADE_OUT = 0.35; // yaşamın ne kadarı yumuşak sönmede

const SPARKLE_CHARS = ["*", "+", "x", "=", "o", "0", "#", "•", "·", "~", "^"];

const CELL = 12;
const PAD = 4;

const BackgroundSparkles = () => {
  const { theme } = useTheme();
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return undefined;

    const accent = ACCENT[theme] || ACCENT.dark;

    const state = {
      w: 0,
      h: 0,
      cols: 0,
      rows: 0,
      cell: CELL,
      sparkles: [],
      sparkle_timer: 0,
    };

    const spawnSparkle = () => {
      const col = PAD + Math.floor(Math.random() * Math.max(1, state.cols - PAD * 2));
      const row = PAD + Math.floor(Math.random() * Math.max(1, state.rows - PAD * 2));
      const ch = SPARKLE_CHARS[Math.floor(Math.random() * SPARKLE_CHARS.length)];
      state.sparkles.push({
        col,
        row,
        ch,
        alpha:
          SPARKLE_ALPHA_MIN +
          Math.random() * (SPARKLE_ALPHA_MAX - SPARKLE_ALPHA_MIN),
        life: SPARKLE_LIFE_MIN + Math.random() * (SPARKLE_LIFE_MAX - SPARKLE_LIFE_MIN),
        age: 0,
      });
    };

    const resize = () => {
      state.w = canvas.width = window.innerWidth;
      state.h = canvas.height = window.innerHeight;
      state.cell = state.w < 768 ? 6 : CELL;
      state.cols = Math.ceil(state.w / state.cell) + PAD * 2;
      state.rows = Math.ceil(state.h / state.cell) + PAD * 2;
      state.sparkles = [];
      state.sparkle_timer = 0;
      ctx.font = `bold ${state.cell}px "Courier New", monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
    };

    let disposed = false;
    let rafId = 0;
    let lastTime = performance.now();

    const loop = () => {
      if (disposed) return;
      ctx.clearRect(0, 0, state.w, state.h);
      const now = performance.now();
      const dt = Math.min(0.05, (now - lastTime) / 1000);
      lastTime = now;

      state.sparkle_timer += dt;
      const interval = 1 / SPARKLE_RATE;
      while (state.sparkle_timer >= interval) {
        state.sparkle_timer -= interval;
        if (state.sparkles.length < SPARKLE_MAX) spawnSparkle();
      }
      for (let i = state.sparkles.length - 1; i >= 0; i--) {
        const sp = state.sparkles[i];
        sp.age += dt;
        if (sp.age >= sp.life) state.sparkles.splice(i, 1);
      }

      const offset = state.cell / 2;
      const { cols, rows } = state;
      const ar = accent.r;
      const ag = accent.g;
      const ab = accent.b;
      for (let i = 0; i < state.sparkles.length; i++) {
        const sp = state.sparkles[i];
        const t = sp.age / sp.life;
        let alpha = sp.alpha;
        if (t < SPARKLE_FADE_IN) alpha *= t / SPARKLE_FADE_IN;
        else if (t > 1 - SPARKLE_FADE_OUT) alpha *= (1 - t) / SPARKLE_FADE_OUT;
        if (sp.col < 0 || sp.col >= cols || sp.row < 0 || sp.row >= rows) continue;
        const sx = (sp.col - PAD) * state.cell + offset;
        const sy = (sp.row - PAD) * state.cell + offset;
        ctx.fillStyle = `rgba(${ar}, ${ag}, ${ab}, ${alpha})`;
        ctx.fillText(sp.ch, sx, sy);
      }

      rafId = requestAnimationFrame(loop);
    };

    resize();
    window.addEventListener("resize", resize);
    rafId = requestAnimationFrame(loop);

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
};

export default BackgroundSparkles;
